import { useQuery } from "react-query";
import {
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Download,
  ExternalLink,
  Mail,
  Package,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import type { DomainInfo } from "../types";
import LoadingSpinner from "../components/LoadingSpinner";
import MessageCard from "../components/MessageCard";
import LicenseRequestForm from "../components/LicenseRequestForm";

function extractDomain(email: string): string | null {
  const parts = email.split("@");
  if (parts.length !== 2 || !parts[1]) return null;
  return parts[1].toLowerCase();
}

export default function LicensePage() {
  const { user } = useAuth();

  // Member resolution does NOT depend on the domain lookup. The query
  // only runs for users we've confirmed are Guests with an email domain.
  const isGuest = user?.userType === "Guest";
  const domain = isGuest && user?.email ? extractDomain(user.email) : null;

  const domainQuery = useQuery<DomainInfo | null>(
    ["domainInfo", domain],
    async () => {
      if (!domain) return null;
      try {
        const res = await apiService.getDomainInfo(domain);
        if (res.data?.success && res.data.data) {
          return res.data.data as DomainInfo;
        }
        return null;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    {
      enabled: isGuest && !!domain,
      retry: false,
    },
  );

  if (!user) {
    return (
      <ErrorPersona message="You are not signed in. Please log in to continue." />
    );
  }

  if (user.userType === "Member") {
    return <MemberPersona />;
  }

  if (user.userType === "Guest") {
    if (domainQuery.isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner />
        </div>
      );
    }
    if (domainQuery.isError) {
      return (
        <ErrorPersona message="We could not determine your license persona. Please try again later." />
      );
    }
    if (domainQuery.data) {
      return (
        <GuestKnownPersona
          domain={domainQuery.data.domain}
          ready={domainQuery.data.ready}
        />
      );
    }
    return <GuestUnknownPersona email={user.email} />;
  }

  return (
    <ErrorPersona message="Your account type could not be determined. Contact the platform team." />
  );
}

function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        {title}
      </h1>
      <p className="mt-1 text-gray-600 dark:text-gray-300">{subtitle}</p>
    </div>
  );
}

function MemberPersona() {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title="Claude Enterprise License"
        subtitle="Claude Enterprise is now generally available at Sandvik. Order or manage your license through the ServiceNow portal."
      />
      <MessageCard
        icon={CheckCircle2}
        title="Claude Enterprise is now live"
        tone="info"
      >
        Claude Enterprise is generally available within Sandvik. If you
        pre-ordered a license or are part of the beta group, you already have
        access.
      </MessageCard>
      <div className="grid gap-6 lg:grid-cols-2">
        <MessageCard icon={Download} title="Getting started">
          Download the Claude apps from Company Portal, or log in at{" "}
          <a
            href="https://claude.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 underline"
          >
            claude.ai
          </a>{" "}
          using SSO with your Sandvik account. The SSO button can take a moment
          to appear on the login page, so wait a bit if you do not see it right
          away.
          <p className="mt-2">
            <span className="font-semibold">Windows users:</span> you need to
            restart Claude after installing for Cowork to work.
          </p>
        </MessageCard>
        <MessageCard icon={Package} title="What you get">
          One license gives you access to four tools, and the license costs the
          same no matter how many of them you use: Claude Chat, Claude Code,
          Claude Cowork, and Claude Design. You select the tools you want in the
          order form.
        </MessageCard>
        <MessageCard
          icon={DollarSign}
          title="Usage limit: default is $25 per month"
        >
          Every license starts with a usage limit of $25 per user per month.
          This default applies unless you set your own cap. Usage is a
          pass-through cost on top of the license (350 SEK per user per month),
          charged to your cost center based on actual consumption, with no
          markup. You only pay for what you actually use, up to your limit.
          <p className="mt-2">
            If you need more than $25 per month, request a higher limit through
            the service portal. A higher limit requires approval from your cost
            approver. Your current limit is visible in your Claude account.
          </p>
          <div className="mt-3">
            <a
              href="https://serviceportal.sandvik.com/esc?id=sc_cat_item&table=sc_cat_item&sys_id=9a3ac9712b70cf505826f23cf291bfe1&searchTerm=claude%20enterprise%20usage"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline inline-flex items-center gap-2"
            >
              Request a higher usage limit
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </MessageCard>
        <MessageCard
          icon={ExternalLink}
          title="Order or change your license"
          tone="info"
          action={
            <a
              href="https://serviceportal.sandvik.com/esc?id=sc_cat_item_guide&table=sc_cat_item&sys_id=27882eb62b0c83900766f0b16e91bf67&searchTerm=claude%20enterprise"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary inline-flex items-center gap-2"
            >
              Open the order form
              <ExternalLink className="h-4 w-4" />
            </a>
          }
        >
          If you have not ordered yet, or want to change which tools you have,
          use the order form in the ServiceNow portal.
        </MessageCard>
      </div>
      <MessageCard
        icon={ShieldCheck}
        title="What you can and cannot share with Claude"
        tone="warning"
      >
        Claude Enterprise is approved for use up to Confidential (i3) and is
        intended for internal, non-sensitive work only. You must not input:
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>I4 (Severe) classified information</li>
          <li>Customer data</li>
          <li>
            Personal data (names, personnel numbers, health data, or anything
            covered by GDPR)
          </li>
          <li>
            Credentials and secrets (passwords, API keys, certificates, tokens)
          </li>
          <li>Export-controlled or classified information</li>
        </ul>
        <p className="mt-2">
          Internal source code, technical documentation, business documents,
          reports, and general internal content up to Confidential (i3) are
          fine, as long as they do not fall into the categories above. If you
          are unsure, check the data classification or ask your security
          representative.
        </p>
      </MessageCard>
    </div>
  );
}

interface GuestKnownPersonaProps {
  domain: string;
  ready: boolean;
}

function GuestKnownPersona({ domain, ready }: GuestKnownPersonaProps) {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Request a Claude Enterprise License"
        subtitle={`You are a guest from ${domain}.`}
      />
      <MessageCard
        icon={CheckCircle2}
        title="Claude Enterprise is now available"
        tone="info"
      >
        Claude is now Generally Available within Sandvik. Submitting this form
        registers your interest — your license will be activated according to
        your company's setup. We are aligning with the IT department of each
        non-integrated company, so activation may be delayed for some companies
        depending on those conversations.
      </MessageCard>
      {!ready && (
        <MessageCard
          icon={AlertTriangle}
          tone="warning"
          title="Your company is not yet onboarded"
        >
          We are still aligning with your company's IT department, so license
          activation for your company may take additional time. You can still
          register your interest below.
        </MessageCard>
      )}
      <LicenseRequestForm companyLabel={domain} />
    </div>
  );
}

interface GuestUnknownPersonaProps {
  email: string;
}

function GuestUnknownPersona({ email }: GuestUnknownPersonaProps) {
  return (
    <div className="space-y-6">
      <PageHeader
        title="License Requests"
        subtitle="Your email domain is not linked to a known company in this system."
      />
      <MessageCard
        icon={Mail}
        title="Contact the platform team"
        tone="warning"
        action={
          <a
            href="mailto:petter.viippola@sandvik.com**"
            className="btn btn-outline inline-flex items-center gap-2"
          >
            Email the platform team
            <Mail className="h-4 w-4" />
          </a>
        }
      >
        Your account (<span className="font-mono text-xs">{email}</span>) is not
        mapped to any non-integrated company in ProdAtlas. If you believe this
        is an error, reach out to the platform team to get your domain
        registered.
      </MessageCard>
    </div>
  );
}

interface ErrorPersonaProps {
  message: string;
}

function ErrorPersona({ message }: ErrorPersonaProps) {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        License Requests
      </h1>
      <MessageCard
        icon={AlertTriangle}
        title="Something went wrong"
        tone="danger"
      >
        {message}
      </MessageCard>
    </div>
  );
}
